import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPartnersSummary, getTierAlerts, getLeaderboard, updatePartner } from '../api';
import type { PartnerSummary, TierAlert, Leaderboard, PartnerTier, PartnerStatus, TerritoryStatus } from '../types';
import RevenueChart from '../components/RevenueChart';
import CircularProgress from '../components/CircularProgress';
import AdminGuide from '../components/AdminGuide';

const CERTIFIED_THRESHOLD = 50000;

export default function Dashboard() {
  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [alerts, setAlerts] = useState<TierAlert[]>([]);
  const [leaderboard, setLeaderboard] = useState<Leaderboard | null>(null);
  const [threshold, setThreshold] = useState<number>(CERTIFIED_THRESHOLD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editPartner, setEditPartner] = useState<PartnerSummary | null>(null);
  const [editTier, setEditTier] = useState<PartnerTier>('Associate');
  const [editTerritory, setEditTerritory] = useState('');
  const [editStatus, setEditStatus] = useState<PartnerStatus>('Active');
  const [editTerritoryStatus, setEditTerritoryStatus] = useState<TerritoryStatus>('Active');
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([getPartnersSummary(), getTierAlerts(), getLeaderboard()])
      .then(([summary, a, lb]) => {
        setPartners(summary.partners);
        setAlerts(a.alerts);
        setThreshold(a.threshold);
        setLeaderboard(lb);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const openEdit = (p: PartnerSummary) => {
    setEditPartner(p);
    setEditTier(p.tier);
    setEditTerritory(p.state_territory);
    setEditStatus(p.status);
    setEditTerritoryStatus(p.territory_status ?? 'Active');
  };

  const closeEdit = () => {
    setEditPartner(null);
  };

  const saveEdit = async () => {
    if (!editPartner) return;
    setSaving(true);
    try {
      await updatePartner(String(editPartner.id), {
        tier: editTier,
        state_territory: editTerritory,
        status: editStatus,
        territory_status: editTerritoryStatus,
      });
      closeEdit();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gold/80">Loading…</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;

  const handlePrintLeaderboard = () => window.print();

  return (
    <div className="space-y-8">
      <div className="no-print flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gold">Performance Portal</h1>
          <p className="text-station-muted mt-1">Territory partners, rankings, and advancement.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/register"
            className="inline-flex items-center rounded-lg bg-gold text-charcoal font-medium px-4 py-2 hover:bg-gold/90 transition-colors"
          >
            Partner Registration
          </Link>
          <Link
            to="/admin/screening"
            className="text-sm font-medium text-gold/90 hover:text-gold transition-colors"
          >
            Admin · Screening
          </Link>
          <Link
            to="/admin/users"
            className="text-sm font-medium text-gold/90 hover:text-gold transition-colors"
          >
            Admin · Users
          </Link>
        </div>
      </div>

      {/* Reports: Revenue chart from leaderboard */}
      <section className="no-print rounded-xl border border-leather bg-leather/40 p-5">
        <h2 className="text-lg font-semibold text-gold mb-4">Reports</h2>
        <RevenueChart
          data={leaderboard?.top_territory_producers ?? []}
          quarter={leaderboard?.quarter}
        />
      </section>

      {/* Quarterly Performance Leaderboard */}
      <section className="leaderboard-print-area rounded-xl border border-leather bg-leather/40 p-5">
        <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gold">Quarterly Rankings</h2>
          <button
            type="button"
            onClick={handlePrintLeaderboard}
            className="rounded-lg bg-gold text-charcoal font-medium px-4 py-2 hover:bg-gold/90 transition-colors"
          >
            Print Leaderboard
          </button>
        </div>
        <h2 className="print-only text-lg font-semibold text-gold mb-2">Quarterly Rankings · {leaderboard?.quarter ?? '—'}</h2>
        <p className="no-print text-station-muted text-sm mb-4">{leaderboard?.quarter ?? '—'}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gold/90 mb-2">Top 3 Territory Producers</h3>
            <ul className="space-y-2">
              {(leaderboard?.top_territory_producers ?? []).length === 0 ? (
                <li className="text-station-muted text-sm">No data this quarter</li>
              ) : (
                leaderboard?.top_territory_producers?.map((e, i) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-gold font-medium w-6">#{i + 1}</span>
                    <Link to={`/partners/${e.id}`} className="text-white hover:text-gold truncate flex-1">
                      {e.name}
                    </Link>
                    <span className="text-gold shrink-0">${(e.total_revenue ?? 0).toLocaleString()}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gold/90 mb-2">Fastest Territory Growth</h3>
            {!leaderboard?.fastest_territory_growth ? (
              <p className="text-station-muted text-sm">No prior quarter to compare</p>
            ) : (
              <div className="flex items-center justify-between gap-3 text-sm">
                <Link
                  to={`/partners/${leaderboard.fastest_territory_growth.id}`}
                  className="text-white hover:text-gold"
                >
                  {leaderboard.fastest_territory_growth.name}
                </Link>
                <span className="text-gold font-medium">
                  +{Number(leaderboard.fastest_territory_growth.growth_pct ?? 0).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      <AdminGuide />

      {alerts.length > 0 && (
        <section className="no-print rounded-xl border border-gold/50 bg-gold-muted p-4">
          <h2 className="font-semibold text-gold flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse" />
            Advancement – eligible for Certified Broker
          </h2>
          <p className="text-sm text-gold/80 mt-1">
            Associates with total collected revenue ≥ ${threshold.toLocaleString()}.
          </p>
          <ul className="mt-3 space-y-2">
            {alerts.map((a) => (
              <li key={a.partner_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <Link
                  to={`/partners/${a.partner_id}`}
                  className="font-medium text-gold hover:underline"
                >
                  {a.name}
                </Link>
                <span className="text-gold/70">{a.state_territory}</span>
                <span className="text-gold/80">
                  ${a.total_collected_revenue.toLocaleString()} collected
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="no-print">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gold">All partners</h2>
          <Link
            to="/partners/new"
            className="text-sm font-medium text-gold hover:text-gold-light transition-colors"
          >
            + Add partner
          </Link>
        </div>
        <div className="rounded-lg overflow-x-auto border border-leather bg-leather/30 all-partners-table">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-leather text-gold/90 text-sm">
              <tr>
                <th className="px-4 py-3 font-medium">Progress</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Email</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium">Territory</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-leather/50">
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-station-muted">
                    No partners yet. Add a partner to get started.
                  </td>
                </tr>
              ) : (
                partners.map((p) => {
                  const progressPct =
                    p.tier === 'Associate'
                      ? Math.min(100, (p.total_collected_revenue / CERTIFIED_THRESHOLD) * 100)
                      : 100;
                  return (
                    <tr key={p.id} className="hover:bg-leather/20">
                      <td className="px-4 py-3">
                        <CircularProgress
                          progress={progressPct}
                          size={36}
                          strokeWidth={3}
                          showOnlyForTier="Associate"
                          tier={p.tier}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-white">{p.name}</td>
                      <td className="px-4 py-3 text-station-muted hidden md:table-cell">{p.email}</td>
                      <td className="px-4 py-3">
                        <span className={p.tier === 'Certified Broker' ? 'text-gold font-medium' : 'text-station-muted'}>
                          {p.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-station-muted">{p.state_territory}</td>
                      <td className="px-4 py-3">
                        <span className={p.status === 'Active' ? 'text-green-400' : 'text-amber-400'}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex items-center gap-2">
                        <Link
                          to={`/partners/${p.id}`}
                          className="text-gold hover:text-gold-light text-sm font-medium"
                        >
                          View
                        </Link>
                        <button
                          type="button"
                          onClick={() => openEdit(p)}
                          className="text-gold/80 hover:text-gold text-sm font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Edit Partner modal */}
        {editPartner && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto" onClick={closeEdit}>
            <div
              className="rounded-xl border border-leather bg-charcoal p-6 w-full max-w-md min-w-0 my-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gold mb-4">Edit partner: {editPartner.name}</h3>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gold/90 mb-1">Tier</label>
                  <select
                    value={editTier}
                    onChange={(e) => setEditTier(e.target.value as PartnerTier)}
                    className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white focus:ring-2 focus:ring-gold"
                  >
                    <option value="Associate">Associate</option>
                    <option value="Certified Broker">Certified Broker</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gold/90 mb-1">Territory</label>
                  <input
                    type="text"
                    value={editTerritory}
                    onChange={(e) => setEditTerritory(e.target.value)}
                    className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white focus:ring-2 focus:ring-gold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gold/90 mb-1">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as PartnerStatus)}
                    className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white focus:ring-2 focus:ring-gold"
                  >
                    <option value="Active">Active</option>
                    <option value="Review">Review</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gold/90 mb-1">Territory status</label>
                  <select
                    value={editTerritoryStatus}
                    onChange={(e) => setEditTerritoryStatus(e.target.value as TerritoryStatus)}
                    className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white focus:ring-2 focus:ring-gold"
                  >
                    <option value="Active">Active</option>
                    <option value="At Risk">At Risk</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-4 py-2 rounded-lg border border-leather text-station-muted hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold/90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Mobile card view: hides on md+ via CSS media query */}
        <div className="all-partners-cards space-y-3">
          {partners.length === 0 ? (
            <div className="rounded-lg border border-leather bg-leather/30 px-4 py-6 text-center text-station-muted">
              No partners yet. Add a partner to get started.
            </div>
          ) : (
            partners.map((p) => {
              const progressPct =
                p.tier === 'Associate'
                  ? Math.min(100, (p.total_collected_revenue / CERTIFIED_THRESHOLD) * 100)
                  : 100;
              const statusClass =
                p.status === 'Active'
                  ? 'bg-green-500/15 text-green-300 border border-green-400/40'
                  : 'bg-amber-500/15 text-amber-300 border border-amber-400/40';

              return (
                <div
                  key={p.id}
                  className="rounded-xl border border-leather bg-leather/40 px-4 py-4 flex flex-col gap-3"
                >
                  {/* Header: name + status badge */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-white truncate">{p.name}</div>
                      <div className="text-xs text-station-muted">
                        {p.state_territory} ·{' '}
                        <span className={p.tier === 'Certified Broker' ? 'text-gold font-medium' : ''}>
                          {p.tier}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                      {p.status}
                    </span>
                  </div>

                  {/* Progress ring */}
                  <div className="flex flex-col items-center justify-center mt-2">
                    <CircularProgress
                      progress={progressPct}
                      size={56}
                      strokeWidth={4}
                      showOnlyForTier="Associate"
                      tier={p.tier}
                      className="mb-1"
                    />
                    <span className="text-xs text-station-muted">
                      {p.tier === 'Associate'
                        ? 'Progress to Certified Broker'
                        : 'Certified Broker · 5% rate'}
                    </span>
                  </div>

                  {/* View Performance Portal button */}
                  <Link
                    to={`/portal/${p.id}`}
                    className="mt-2 inline-flex items-center justify-center rounded-lg bg-gold text-charcoal font-medium text-sm py-2 w-full shadow-sm hover:bg-gold-light transition-colors"
                  >
                    View Performance Portal
                  </Link>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
